import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const API_BASE = 'https://api.pacificpearltraining.site';
const DEFAULT_RESTAURANT = 'Peking Chinese Restaurant';
const RESTAURANTS = [
  'Peking Chinese Restaurant',
  'China Wok',
  'Excel Chinese Food',
  'Great Wall',
  'To-Kai Sushi',
  'First Wok NY Style',
  "Top's China",
  "Top's China - Prince George",
  'No1 Wok Restaurant',
  'China Wang',
  'New China Restaurant',
  'Mei Wei Restaurant'
];

const Page = styled.div`
  min-height: 100vh;
  background: #f6f7f9;
  color: #1f2933;
  padding: 24px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.8rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #cfd7df;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 260px;
  background: #fff;
`;

const Button = styled.button`
  padding: 8px 12px;
  border: 1px solid ${props => props.$danger ? '#b42318' : '#1d4ed8'};
  border-radius: 6px;
  background: ${props => props.$secondary ? '#fff' : props.$danger ? '#b42318' : '#1d4ed8'};
  color: ${props => props.$secondary ? '#1d4ed8' : '#fff'};
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 42%) 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  overflow: hidden;
`;

const ReviewRow = styled.button`
  width: 100%;
  display: block;
  padding: 12px 14px;
  border: 0;
  border-bottom: 1px solid #e5e9ef;
  background: ${props => props.$selected ? '#e8f1ff' : '#fff'};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f3f7fd;
  }
`;

const RowTitle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-weight: 700;
  font-size: 0.95rem;
`;

const RowMeta = styled.div`
  margin-top: 4px;
  color: #667085;
  font-size: 0.8rem;
`;

const Status = styled.span`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${props => props.$invalid ? '#fee4e2' : props.$done ? '#dcfce7' : '#fff7cc'};
  color: ${props => props.$invalid ? '#b42318' : props.$done ? '#166534' : '#854d0e'};
  font-size: 0.75rem;
  white-space: nowrap;
`;

const Detail = styled.div`
  padding: 16px;
`;

const SectionTitle = styled.h2`
  margin: 18px 0 8px;
  font-size: 1rem;
`;

const TextBlock = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e5e9ef;
  border-radius: 6px;
  font: inherit;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const ItemList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString();
}

function getStatusFlags(review) {
  const status = review?.status || '';
  return {
    invalid: status.includes('invalid'),
    done: status.includes('reviewed') || status.includes('resolved')
  };
}

function summarizeItem(item) {
  if (!item) return '';
  const parts = [
    item.quantity ? `${item.quantity}x` : null,
    item.title,
    item.variantTitle ? `(${item.variantTitle})` : null,
    item.sku ? `[${item.sku}]` : null
  ].filter(Boolean);
  return parts.join(' ');
}

function getInitialRestaurant() {
  const params = new URLSearchParams(window.location.search);
  const restaurantFromUrl = params.get('restaurant');
  if (restaurantFromUrl && RESTAURANTS.includes(restaurantFromUrl)) return restaurantFromUrl;
  return DEFAULT_RESTAURANT;
}

export default function TrainerReviews() {
  const [restaurant, setRestaurant] = useState(getInitialRestaurant);
  const [reviews, setReviews] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionStatus, setActionStatus] = useState('');

  const selectedReview = useMemo(
    () => reviews.find(review => review.id === selectedId) || reviews[0] || null,
    [reviews, selectedId]
  );

  const loadReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ restaurant, maxOrders: '200' });
      const response = await fetch(`${API_BASE}/training-reviews?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load reviews.');
      setReviews(payload.reviews || []);
      setSelectedId(payload.reviews?.[0]?.id || null);
      window.history.replaceState(null, '', `/trainer/reviews?restaurant=${encodeURIComponent(restaurant)}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  const submitTrainerDecision = async (status, decision) => {
    if (!selectedReview) return;
    setActionStatus('Saving...');
    try {
      const response = await fetch(`${API_BASE}/training-reviews/${selectedReview.traineeOrderId}/${selectedReview.id}/trainer-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, decision, trainerName: 'Trainer' })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Could not save trainer decision.');
      setActionStatus('Saved.');
      await loadReviews();
    } catch (err) {
      setActionStatus(err.message);
    }
  };

  const statusFlags = getStatusFlags(selectedReview);
  const traineeItems = selectedReview?.comparisonSummary?.unmatchedTraineeItems || [];
  const originalItems = selectedReview?.comparisonSummary?.unmatchedOriginalItems || [];

  return (
    <Page>
      <Header>
        <div>
          <Title>Trainer Reviews</Title>
          <div>{reviews.length} saved review request{reviews.length === 1 ? '' : 's'}</div>
        </div>
        <FilterBar>
          <Select value={restaurant} onChange={event => setRestaurant(event.target.value)}>
            {RESTAURANTS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
          <Button onClick={loadReviews} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</Button>
        </FilterBar>
      </Header>

      {error && <TextBlock>{error}</TextBlock>}

      <Layout>
        <Panel>
          {reviews.map(review => {
            const flags = getStatusFlags(review);
            const traineeName = `${review.trainee?.firstName || review.traineeInfo?.firstName || ''} ${review.trainee?.lastName || review.traineeInfo?.lastName || ''}`.trim();
            return (
              <ReviewRow key={review.id} $selected={selectedReview?.id === review.id} onClick={() => setSelectedId(review.id)}>
                <RowTitle>
                  <span>{traineeName || 'Unknown trainee'}</span>
                  <Status $invalid={flags.invalid} $done={flags.done}>{review.status || 'pending'}</Status>
                </RowTitle>
                <RowMeta>{review.issueLabel || review.issueType || 'No issue type'} - Order {review.traineeOrderName || review.traineeOrderId}</RowMeta>
                <RowMeta>{formatDate(review.createdAt)}</RowMeta>
              </ReviewRow>
            );
          })}
          {!loading && reviews.length === 0 && <Detail>No review requests found for this filter.</Detail>}
        </Panel>

        <Panel>
          {selectedReview ? (
            <Detail>
              <RowTitle>
                <span>{selectedReview.issueLabel || selectedReview.issueType}</span>
                <Status $invalid={statusFlags.invalid} $done={statusFlags.done}>{selectedReview.status}</Status>
              </RowTitle>
              <RowMeta>
                Trainee order {selectedReview.traineeOrderName || selectedReview.traineeOrderId}
                {' '}vs real order {selectedReview.originalOrderName || selectedReview.originalOrderId}
              </RowMeta>

              <SectionTitle>Trainee Note</SectionTitle>
              <TextBlock>{selectedReview.reviewNote || '(empty)'}</TextBlock>

              <SectionTitle>AI Note Check</SectionTitle>
              <TextBlock>{selectedReview.noteValidation?.feedbackToTrainee || selectedReview.noteValidation?.reason || '(not checked)'}</TextBlock>

              <SectionTitle>Transcript / Script</SectionTitle>
              <TextBlock>{selectedReview.savedTranscript || '(not available)'}</TextBlock>

              <SectionTitle>Unmatched Trainee Items</SectionTitle>
              <ItemList>
                {traineeItems.map((item, index) => <li key={`${item.sku}-${index}`}>{summarizeItem(item)}</li>)}
                {traineeItems.length === 0 && <li>None</li>}
              </ItemList>

              <SectionTitle>Unmatched Real Order Items</SectionTitle>
              <ItemList>
                {originalItems.map((item, index) => <li key={`${item.sku}-${index}`}>{summarizeItem(item)}</li>)}
                {originalItems.length === 0 && <li>None</li>}
              </ItemList>

              <ActionBar>
                <Button onClick={() => submitTrainerDecision('trainer_confirmed_system_issue', 'system_issue')}>
                  Confirm System Issue
                </Button>
                <Button $secondary onClick={() => submitTrainerDecision('trainer_confirmed_trainee_error', 'trainee_error')}>
                  Mark Trainee Error
                </Button>
                <Button $danger onClick={() => submitTrainerDecision('trainer_rejected_invalid_note', 'invalid_note')}>
                  Reject Note
                </Button>
              </ActionBar>
              {actionStatus && <RowMeta>{actionStatus}</RowMeta>}
            </Detail>
          ) : (
            <Detail>Select a review request.</Detail>
          )}
        </Panel>
      </Layout>
    </Page>
  );
}
